import { createFileRoute, redirect, Link } from "@tanstack/react-router";
import { useAuth } from "../../../components/AuthProvider";
import { createRouteError } from "../../../components/RouteErrorBoundary";
import { PageSkeleton } from "../../../components/RouteSkeleton";
import { useHealthOverview } from "../../../hooks/useHealthCoaching";
import type { HealthMetricEntry } from "../../../server/health-coaching";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { queryKeys, STALE_TIMES } from "../../../lib/queryClient";
import { getHealthOverview } from "../../../server/health-coaching";
import type { RouterContextWithSession } from "../../../router";

export const Route = createFileRoute("/_admin/health-coaching/")({
  beforeLoad: async ({ context }) => {
    if (typeof window === "undefined") return {};
    const sb = context.supabase;
    if (!sb) return {};
    const {
      data: { session },
    } = await sb.auth.getSession();
    if (!session) {
      throw redirect({ to: "/auth" });
    }
    return { session };
  },
  loader: async ({ context }) => {
    if (typeof window === "undefined") return;
    const { session } = context as RouterContextWithSession;
    if (!session) return;

    await context.queryClient.ensureQueryData({
      queryKey: queryKeys.healthCoaching.overview(),
      queryFn: async () => {
        const result = await getHealthOverview({
          data: { accessToken: session.access_token },
        });
        if (result.error) throw new Error(result.error);
        return result.data;
      },
      staleTime: STALE_TIMES.system,
    });
  },
  pendingComponent: () => <PageSkeleton rows={3} variant="cards" />,
  errorComponent: createRouteError("Health Coaching"),
  component: HealthCoachingPage,
});

function HealthCoachingPage() {
  const { session, loading, isAuthenticated } = useAuth();

  if (loading || !isAuthenticated || !session) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return <AuthenticatedHealthCoaching accessToken={session.access_token} />;
}

function AuthenticatedHealthCoaching({ accessToken }: { accessToken: string }) {
  const { data, isLoading, error } = useHealthOverview({ accessToken });

  return (
    <div className="text-foreground">
      <div className="container mx-auto px-4 py-6 max-w-4xl space-y-6">
        <header className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Health Coaching</h1>
            <p className="text-muted-foreground">
              Client health, nutrition, and exercise overview
            </p>
          </div>
          <Button variant="outline" asChild>
            <Link to="/dashboard">Back to Dashboard</Link>
          </Button>
        </header>

        {isLoading && (
          <p className="text-muted-foreground">Loading health data...</p>
        )}
        {error && (
          <p className="text-destructive">Error: {error.message}</p>
        )}

        {!isLoading && !error && data && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <p className="text-xs text-muted-foreground">Active Users</p>
                  <p className="text-2xl font-semibold">{data.totalUsers}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <p className="text-xs text-muted-foreground">Avg Energy</p>
                  <p className="text-2xl font-semibold">{data.avgEnergy}/10</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <p className="text-xs text-muted-foreground">Avg Sleep</p>
                  <p className="text-2xl font-semibold">{data.avgSleep}h</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <p className="text-xs text-muted-foreground">Recent Entries</p>
                  <p className="text-2xl font-semibold">{data.recentMetrics.length}</p>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Recent Health Metrics</CardTitle>
                <CardDescription>Latest health data from clients</CardDescription>
              </CardHeader>
              <CardContent>
                {data.recentMetrics.length === 0 && (
                  <p className="text-muted-foreground">No health metrics recorded yet.</p>
                )}
                {data.recentMetrics.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 px-3 font-medium text-muted-foreground">Date</th>
                          <th className="text-left py-2 px-3 font-medium text-muted-foreground">Sleep</th>
                          <th className="text-left py-2 px-3 font-medium text-muted-foreground">Energy</th>
                          <th className="text-left py-2 px-3 font-medium text-muted-foreground">Mood</th>
                          <th className="text-left py-2 px-3 font-medium text-muted-foreground">Steps</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.recentMetrics.map((m: HealthMetricEntry) => (
                          <tr key={m.id} className="border-b last:border-0">
                            <td className="py-2 px-3">{new Date(m.date).toLocaleDateString()}</td>
                            <td className="py-2 px-3">{m.sleep_hours != null ? `${m.sleep_hours}h` : "—"}</td>
                            <td className="py-2 px-3">{m.energy_level != null ? `${m.energy_level}/10` : "—"}</td>
                            <td className="py-2 px-3">{m.mood ?? "—"}</td>
                            <td className="py-2 px-3">{m.steps != null ? m.steps.toLocaleString() : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
